import { useMemo, useState } from 'react'
import { PageContainer, PageHeader } from '../../components/Layout'
import { Button, Card, CardContent, Input, Select, Badge } from '../../components/UI'
import { rentalTechService } from '../../services/api'
import { equipmentOptions, useRentalTechData } from './useRentalTechData'

export default function RentalTechTelemetry() {
  const { telemetry, equipments, refresh } = useRentalTechData()
  const [riskByEquipment, setRiskByEquipment] = useState({})
  const [form, setForm] = useState({
    equipmentId: '',
    engineHours: 286,
    lat: 4.82,
    lng: -74.31,
    temperatureC: 98,
    vibrationMmS: 7.6
  })

  const options = useMemo(() => equipmentOptions(equipments), [equipments])
  if (!form.equipmentId && options.length > 0) {
    setTimeout(() => setForm((prev) => ({ ...prev, equipmentId: options[0].value })), 0)
  }

  const simulate = async () => {
    await rentalTechService.simulateTelemetry({
      ...form,
      engineHours: Number(form.engineHours),
      lat: Number(form.lat),
      lng: Number(form.lng),
      temperatureC: Number(form.temperatureC),
      vibrationMmS: Number(form.vibrationMmS)
    })
    await refresh()
  }

  const predict = async (equipmentId) => {
    const result = await rentalTechService.failureScore(equipmentId)
    setRiskByEquipment((prev) => ({ ...prev, [equipmentId]: result?.data?.data || {} }))
  }

  const equipmentName = (id) => equipments.find((e) => e.id === id)?.name || id

  return (
    <PageContainer>
      <div className="space-y-6">
        <PageHeader
          title="IoT + Telemetria + IA Predictiva"
          subtitle="Horas motor, GPS, temperatura, vibracion y geofencing"
        />

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Card>
            <CardContent className="p-5 space-y-3">
              <h3 className="font-semibold text-gray-900">Simulador de Telemetria</h3>
              <Select options={options} value={form.equipmentId} onChange={(e) => setForm({ ...form, equipmentId: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <Input type="number" value={form.engineHours} onChange={(e) => setForm({ ...form, engineHours: e.target.value })} placeholder="Horas motor" />
                <Input type="number" value={form.vibrationMmS} onChange={(e) => setForm({ ...form, vibrationMmS: e.target.value })} placeholder="Vibracion" />
                <Input type="number" value={form.temperatureC} onChange={(e) => setForm({ ...form, temperatureC: e.target.value })} placeholder="Temperatura" />
                <Input type="number" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} placeholder="Lat" />
                <Input type="number" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} placeholder="Lng" />
              </div>
              <Button onClick={simulate}>Enviar muestra</Button>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5 space-y-3 text-sm">
              <h3 className="font-semibold text-gray-900">Predictor de Falla (mock)</h3>
              {equipments.map((eq) => {
                const risk = riskByEquipment[eq.id]
                return (
                  <div key={eq.id} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-gray-900">{eq.name}</p>
                      <Button size="sm" variant="secondary" onClick={() => predict(eq.id)}>Calcular riesgo</Button>
                    </div>
                    {risk ? (
                      <div className="mt-2">
                        <p>Score: <strong>{risk.risk_score}/100</strong></p>
                        <p className="text-gray-600">{risk.recommendation}</p>
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left">
                  <th className="px-4 py-3">Equipo</th>
                  <th className="px-4 py-3">Horas</th>
                  <th className="px-4 py-3">Temperatura</th>
                  <th className="px-4 py-3">Vibracion</th>
                  <th className="px-4 py-3">GPS</th>
                  <th className="px-4 py-3">Geofence</th>
                </tr>
              </thead>
              <tbody>
                {telemetry.map((item) => (
                  <tr key={item.id} className="border-b">
                    <td className="px-4 py-3">{equipmentName(item.equipment_id)}</td>
                    <td className="px-4 py-3">{item.engine_hours}</td>
                    <td className="px-4 py-3">{item.temperature_c} C</td>
                    <td className="px-4 py-3">{item.vibration_mm_s}</td>
                    <td className="px-4 py-3">{item.lat}, {item.lng}</td>
                    <td className="px-4 py-3">
                      <Badge variant={item.geofence_alert ? 'error' : 'success'}>
                        {item.geofence_alert ? 'Alerta activa' : 'Dentro de zona'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  )
}
